import React, {  } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router';
import styled from 'styled-components/macro';

import { Table, Thead, Tbody, Tr, Th, Td, LinkTr } from '../../../components/Table/Fake';

import { formatDate, toMonetaryAmountString } from '../../../helpers/formatters';
import { distances, colors } from '../../../styles/constants';
import { DiscountWithStatus } from '../../types';
import { Campaign } from '../../../types/discounts';
import { StyledTh, StyledTd, TinyTh, TinyTd } from '../common';
import Icon from '../../../components/Icons';

interface DiscountsTableProps {
    discounts: DiscountWithStatus[];
    campaigns: Campaign[];
}

const DiscountsTable = (
    {
        discounts,
        campaigns,
    }: DiscountsTableProps
) => {
    const { t, i18n } = useTranslation();
    const { accountId } = useParams();
    const history = useHistory();

    const campaignsById = (campaigns || []).reduce((acc, campaign) => {
        if (!campaign.campaign_id) {
            return acc;
        }
        return {
            ...acc,
            [campaign.campaign_id]: campaign,
        };
    }, {} as { [id: string]: Campaign });

    return (
        <Table className="no-wrap">
            <Thead>
                <Tr>
                    <StyledTh className="first-child">{t('marketing.campaign.name')}</StyledTh>
                    <Th className="collapse-1200">{t('marketing.campaign.time_used')}</Th>
                    <Th className="collapse-1200">{t('marketing.campaign.discount_given')}</Th>
                    <Th>{t('marketing.campaign.started')}</Th>
                    <Th>{t('marketing.campaign.ends')}</Th>
                    <Th className="collapse-1500">{t('marketing.campaign.discounts_list.fields.campaign')}</Th>
                    <Th className="last-child">{t('marketing.campaign.status')}</Th>
                    <TinyTh />
                </Tr>
            </Thead>
            <Tbody>
                {discounts.map((discount, index) => {
                    const campaign = discount.campaign_id ? campaignsById[discount.campaign_id] : undefined;
                    const campaignName = campaign ? campaign.name : '';
                    return (
                        <LinkTr
                            key={`${index}-${discount.id}`}
                            to={`/${accountId}/marketing/discounts/${discount.id}`}
                            className="stop-borders-before-edges"
                        >
                            <StyledTd className="first-child">{discount.name}</StyledTd>
                            <Td className="collapse-1200">{discount.statistics?.used ?? 0}</Td>
                            <Td className="collapse-1200">
                                {discount.statistics?.amount
                                    ? toMonetaryAmountString(i18n.language, discount.statistics.amount, '')
                                    : 0}
                            </Td>
                            <Td>
                                {formatDate(new Date(discount.requirement.purchase_from).toISOString(), i18n.language)}
                            </Td>
                            <Td>
                                {formatDate(new Date(discount.requirement.purchase_to).toISOString(), i18n.language)}
                            </Td>
                            <Td className="collapse-1500 ellipsis">{campaignName}</Td>
                            <Td>
                                <Badge className={discount.status}>
                                    {t('marketing.campaign.status', { context: discount.status })}
                                </Badge>
                            </Td>
                            <TinyTd>
                                <EmptyButton onClick={(event) => {
                                    event.preventDefault();
                                    history.push(`/${accountId}/marketing/campaigns/copy/${discount.id}`);
                                }}>
                                    <Icon icon="plus" />
                                </EmptyButton>
                            </TinyTd>
                        </LinkTr>
                    );
                })}
            </Tbody>
        </Table>
    );
};

const Badge = styled.span`
    display: inline-block;
    background: rgb(2, 214, 130, 0.2);
    border-radius: 2px;
    font-size: 11px;
    color: ${colors.secondary.child};
    text-align: center;
    line-height: 16px;
    padding: 3px 8px;
    font-weight: 700;
    margin-left: ${distances.small};

    & + & {
        margin-left: ${distances.tiny};
    }

    &.scheduled {
        color: yellow;
        background: #EABC6F;
    }

    &.completed {
        color: ${colors.background};
        background: ${colors.primaryDisabled};
    }

    &.deleted {
        color: ${colors.background};
        background: ${colors.invalid};
    }
`;

const EmptyButton = styled.button`
    outline: none;
    border: none;
    background: none;
    cursor: pointer;
`;

export default DiscountsTable;
